/**
 * Invite Student Dialog
 *
 * Dialog for inviting a new learner via email.
 * Sends an invitation email with a registration link.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, SelectField, CircularProgress } from '../ui';
import WorkspaceAutocomplete from '../common/WorkspaceAutocomplete';
import { TagAutocomplete } from '../common';
import { usersClient, workspacesClient } from '../../apis/client-instances';
import { TagType, UserRole, Workspace, Tag } from '../../apis/api-client';
import { alert } from '../../hooks/function';
import { RootState } from '../../types';
import { isTagValidForUser, tagScopeForUser } from '../../utils/tagFilters';
import { validateEmail } from '../../utils/validation';

interface InviteStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Workspace the caller is already scoped to (workspace page or global
   *  switcher). When set, the workspace picker is hidden and this id is
   *  used instead of asking the admin to pick it again. */
  lockedWorkspaceId?: number;
}

export default function InviteStudentDialog({ open, onClose, onSuccess, lockedWorkspaceId }: InviteStudentDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const currentUserRole = currentUser?.role;

  const [loading, setLoading] = useState(false);
  // Tracks the resolved Workspace object so the autocomplete can display its
  // name. The id is what we actually submit.
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  // True once we've confirmed at least one workspace exists in the account —
  // used to gate the WorkspaceAdmin role option in the dropdown.
  const [hasAnyWorkspaces, setHasAnyWorkspaces] = useState<boolean>(true);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: UserRole.User,
    workspaceId: undefined as number | undefined,
    selectedTags: [] as Tag[],
  });
  const [errors, setErrors] = useState<any>({});

  // Filter available role options based on current user's role
  const availableRoleOptions = useMemo(() => {
    // Labels follow the Role Model in CLAUDE.md — the API enum name
    // `AccountAdmin` is legacy for the Account Admin role.
    let allRoles = [
      { label: 'Learner', value: UserRole.User },
      { label: 'Workspace Admin', value: UserRole.WorkspaceAdmin },
      { label: 'Account Admin', value: UserRole.AccountAdmin },
      { label: 'Super Admin', value: UserRole.Admin },
    ];

    if (!hasAnyWorkspaces) {
      // If no workspaces exist, remove Workspace Admin role
      allRoles = allRoles.filter((role) => role.value !== UserRole.WorkspaceAdmin);
    }

    // Admin can invite everyone
    if (currentUserRole === UserRole.Admin) {
      return allRoles;
    }

    // Account Admin can invite: User, Workspace Admin, Account Admin (no Admin)
    if (currentUserRole === UserRole.AccountAdmin) {
      return allRoles.filter((role) => role.value !== UserRole.Admin);
    }

    // Workspace Admin can invite: User, Workspace Admin only
    if (currentUserRole === UserRole.WorkspaceAdmin) {
      return allRoles.filter((role) => role.value === UserRole.User || role.value === UserRole.WorkspaceAdmin);
    }

    // Default: only User role (shouldn't normally happen)
    return [{ label: 'User', value: UserRole.User }];
  }, [currentUserRole, hasAnyWorkspaces]);

  // When the dialog opens for an account-level admin, do a lightweight ping to
  // know whether any workspace exists at all (drives whether WorkspaceAdmin is
  // an offer-able role). We don't load the whole list anymore.
  useEffect(() => {
    if (!open) return;
    if ([UserRole.Admin, UserRole.AccountAdmin].includes(currentUser?.role)) {
      workspacesClient
        .searchWorkspaces(undefined, 1, 1)
        .then((res) => setHasAnyWorkspaces((res.totalCount ?? 0) > 0))
        .catch(() => setHasAnyWorkspaces(false));
    }

    if (currentUser?.role === UserRole.WorkspaceAdmin) handleChange('workspaceId', currentUser?.workspaceId);
    else if (lockedWorkspaceId) handleChange('workspaceId', lockedWorkspaceId);
  }, [open, currentUser?.role, lockedWorkspaceId]);

  const handleChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };
    const newErrors = { ...errors, [field]: '' };

    // Clear workspace when changing to Account Admin or Admin role
    if (field === 'role' && (value === UserRole.AccountAdmin || value === UserRole.Admin)) {
      newFormData.workspaceId = undefined;
      newErrors.workspaceId = ''; // Clear workspace error
    } else if (field === 'role' && lockedWorkspaceId) {
      // The picker is hidden when locked, so switching back to a
      // workspace-level role must restore the locked workspace itself.
      newFormData.workspaceId = lockedWorkspaceId;
    }

    // Drop selected tags that aren't valid for the new role/workspace combo.
    if (field === 'role' || field === 'workspaceId') {
      newFormData.selectedTags = formData.selectedTags.filter((t) =>
        isTagValidForUser(t, newFormData.role, newFormData.workspaceId)
      );
    }

    setFormData(newFormData);
    setErrors(newErrors);
  };

  const validate = () => {
    const newErrors: any = {};

    // Email validation
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (formData.role === UserRole.WorkspaceAdmin && !formData.workspaceId) {
      newErrors.workspaceId = 'Workspace is required for Workspace Admin role';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      await usersClient.inviteUser({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        workspaceId: formData.workspaceId,
        tagIds: formData.selectedTags.map((tag) => tag.id),
      });

      alert(enqueueSnackbar, 'Invitation sent successfully!', 'success');
      setFormData({ email: '', firstName: '', lastName: '', role: UserRole.User, workspaceId: undefined, selectedTags: [] });
      setSelectedWorkspace(null);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      alert(enqueueSnackbar, error?.response?.data || 'Failed to send invitation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', firstName: '', lastName: '', role: UserRole.User, workspaceId: undefined, selectedTags: [] });
    setSelectedWorkspace(null);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite Student</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            required
          />

          <TextField
            label="First Name"
            fullWidth
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
          />

          <TextField
            label="Last Name"
            fullWidth
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
          />

          <SelectField
            label="Role"
            fullWidth
            value={formData.role}
            onChange={(e) => handleChange('role', Number(e.target.value))}
            options={availableRoleOptions}
            allowEmpty={false}
          />

          {(currentUser?.role === UserRole.AccountAdmin || currentUser?.role === UserRole.Admin) && !lockedWorkspaceId && (
            <WorkspaceAutocomplete
              value={selectedWorkspace}
              onChange={(ws) => {
                setSelectedWorkspace(ws);
                handleChange('workspaceId', ws?.id);
              }}
              size="medium"
              fullWidth
              required={formData.role === UserRole.WorkspaceAdmin}
              label="Workspace"
              placeholder="No workspace"
              error={!!errors.workspaceId}
              helperText={
                errors.workspaceId ||
                (formData.role === UserRole.WorkspaceAdmin
                  ? 'Required for Workspace Admin role'
                  : undefined)
              }
            />
          )}

          <TagAutocomplete
            multiple
            value={formData.selectedTags}
            onChange={(v) => handleChange('selectedTags', (v as Tag[]) ?? [])}
            tagType={TagType.UserTag}
            {...tagScopeForUser(formData.role, formData.workspaceId)}
            size="medium"
            fullWidth
            label="Tags"
            placeholder="Select tags (optional)"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
